import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { DateSubheader, PillColor } from '../DateSubheader/DateSubheader';
import { ShowListing } from '../DirectoryListings/ShowListing/ShowListing';
import CalendarOverlay from '../CalendarOverlay/CalendarOverlay';
import { XIcon } from '../icons';
import IconButton from '../IconButton/IconButton';
import { Show } from '../../api';
import './ShowsDrawer.css';

interface ShowsDrawerProps {
  open: boolean;
  onClose: () => void;
  shows: Show[];
  selectedDate: string | null;
  onDateChange: (date: string) => void;
  availableDates: string[];
  onFocusedShowChange?: (show: Show | null) => void;
}

const formatDateLabel = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const getPillInfo = (dateStr: string): { label: string; color: PillColor } | null => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const date = new Date(dateStr + 'T00:00:00');
  date.setHours(0, 0, 0, 0);

  if (date.getTime() === today.getTime()) {
    return { label: 'Today', color: 'red' };
  }
  if (date.getTime() === tomorrow.getTime()) {
    return { label: 'Tomorrow', color: 'orange' };
  }
  return null;
};

const formatShowDate = (dateStr: string) => {
  return dayjs(dateStr).format('ddd MMM D');
};

export const ShowsDrawer: React.FC<ShowsDrawerProps> = ({
  open,
  onClose,
  shows,
  selectedDate,
  onDateChange,
  availableDates,
  onFocusedShowChange,
}) => {
  const navigate = useNavigate();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const showsForDate = useMemo(() => {
    if (!selectedDate) return [];
    return shows.filter(show => show.date.split('T')[0] === selectedDate);
  }, [shows, selectedDate]);

  // Track scroll position to determine focused card
  const handleScroll = useCallback(() => {
    const scrollEl = contentRef.current;
    if (!scrollEl || showsForDate.length === 0) return;

    const scrollTop = scrollEl.scrollTop;
    const cardHeight = 140; // Card height
    const gap = 16; // Gap between cards
    const cardWithGap = cardHeight + gap;

    // Calculate which card is at the top
    const index = Math.round(scrollTop / cardWithGap);
    const clampedIndex = Math.max(0, Math.min(showsForDate.length - 1, index));

    if (clampedIndex !== focusedIndex) {
      setFocusedIndex(clampedIndex);
    }
  }, [focusedIndex, showsForDate.length]);

  // Notify parent when focused show changes
  useEffect(() => {
    if (onFocusedShowChange && showsForDate.length > 0) {
      onFocusedShowChange(showsForDate[focusedIndex] || null);
    }
  }, [focusedIndex, showsForDate, onFocusedShowChange]);

  // Reset focused index when date changes
  useEffect(() => {
    setFocusedIndex(0);
  }, [selectedDate]);

  // Set up scroll listener
  useEffect(() => {
    const scrollEl = contentRef.current;
    if (!scrollEl) return;

    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const currentDateIndex = selectedDate ? availableDates.indexOf(selectedDate) : -1;
  const canGoPrevious = currentDateIndex > 0;
  const canGoNext = currentDateIndex < availableDates.length - 1;

  const handlePrevious = () => {
    if (canGoPrevious) {
      onDateChange(availableDates[currentDateIndex - 1]);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      onDateChange(availableDates[currentDateIndex + 1]);
    }
  };

  const handleCalendarSelect = (date: dayjs.Dayjs) => {
    const dateStr = date.format('YYYY-MM-DD');
    onDateChange(dateStr);
  };

  const pillInfo = selectedDate ? getPillInfo(selectedDate) : null;

  return (
    <div className={`shows-drawer ${open ? 'shows-drawer--open' : ''}`}>
      <div className="shows-drawer__header">
        <div className="shows-drawer__date-row">
          {selectedDate && (
            <DateSubheader
              dateLabel={formatDateLabel(selectedDate)}
              onPrevious={canGoPrevious ? handlePrevious : undefined}
              onNext={canGoNext ? handleNext : undefined}
              onDateClick={() => setCalendarOpen(true)}
              showNavArrows={true}
              dateClickable={true}
              className="shows-drawer__date-header"
            />
          )}
          <IconButton
            aria-label="Close drawer"
            onClick={onClose}
            icon={<XIcon size="m" tone="white" />}
          />
        </div>
        <div className="shows-drawer__pill-container">
          {pillInfo && (
            <span
              className="shows-drawer__pill"
              style={{
                backgroundColor: pillInfo.color === 'red' ? '#E53935' : '#FF9800',
              }}
            >
              {pillInfo.label}
            </span>
          )}
        </div>
      </div>

      <div className="shows-drawer__content" ref={contentRef}>
        {showsForDate.length === 0 ? (
          <div className="shows-drawer__empty">
            <p>No shows on this date</p>
          </div>
        ) : (
          showsForDate.map(show => (
            <ShowListing
              key={show.id}
              bandName={show.band_name}
              date={formatShowDate(show.date)}
              venue={show.venue_name || show.address || 'TBD'}
              time={show.time}
              imageUrl={show.band_photo_url}
              soundcloudUrl={show.band_soundcloud_url}
              withMembers={show.band_members}
              onClick={() => navigate(`/shows/${show.id}`)}
            />
          ))
        )}
      </div>

      <CalendarOverlay
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        value={selectedDate ? dayjs(selectedDate) : undefined}
        onSelect={handleCalendarSelect}
        datesWithShows={availableDates}
      />
    </div>
  );
};

export default ShowsDrawer;
